import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState } from '@/components/EmptyState';
import { Avatar } from '@/components/Avatar';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import InviteUserModal from '@/components/InviteUserModal';
import BulkInviteModal from '@/components/BulkInviteModal';
import ManageControls from '@/components/ManageControls';
import RoleDescriptionTooltip from '@/components/RoleDescriptionTooltip';
import { History, Search, UserPlus, Users } from 'lucide-react';
import { fetchRoles, fetchTeamMembers, fetchUserProductActions } from '@/services/teamService';
import { useDebounce } from '@/hooks/useDebounce';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { logError, logDebug } from '@/utils/errorHandler';

// Import the configuration
import { config as appConfig } from '@/config/config';

// Import translation hook
import { useTeampageTranslations } from '@/hooks/useTeampageTranslations';

// Create a local reference to avoid name conflicts
const config = appConfig;

// Tab interface
interface TabItem {
  value: string;
  label: string;
}

interface Membership {
  id: string;
  user:
    | number
    | string
    | {
        id: string | number;
        email?: string;
        name?: string;
        avatar_url?: string;
      }
    | null;
  user_email?: string;
  user_name?: string;
  avatar_url?: string;
  role: {
    id?: string | number;
    name: string;
    description?: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  orgid: string;
  status?: 'active' | 'pending';
  created_at: string;
  updated_at: string;
  invited_at?: string;
  last_login?: string;
}

// Interface for the product activity data from the Activity model
interface ProductActivity {
  id: number;
  entity: string;
  entity_id: string | number;
  action: string;
  message: string;
  created_at?: string;
  timestamp?: string; // Some endpoints return created_at as timestamp
  user?: number;
  user_id?: number;
  user_name?: string;
  organization?: string | number;
  type?: string; // Some serializers rename action as type
  details?: string; // Some serializers rename message as details
}

export const TeamPage: React.FC = () => {
  // Get translations
  const i18n = useTeampageTranslations();

  // State
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkInviteModal, setShowBulkInviteModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(config.team.pagination.defaultPageSize);
  const [orgError, setOrgError] = useState(false);

  // Get auth context and navigation
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Create debounced search value for better UX
  const debouncedSearchText = useDebounce(searchText, 300);

  // Permission-based approach
  const { checkPermission } = useAuth();

  // Check if user has various permissions
  const canInviteUsers = checkPermission('team.invite');

  // Get organization ID from auth context
  const orgID = user?.organization_id;

  // Check if we have a valid organization ID
  useEffect(() => {
    // Only check after auth loading is complete
    if (authLoading) {
      return;
    }

    if (!user) {
      setOrgError(true);
      toast.error(i18n.errors.authenticationRequired);

      // Redirect to login after a delay
      const timer = setTimeout(() => {
        navigate('/login');
      }, 3000);

      return () => clearTimeout(timer);
    }

    if (!orgID) {
      setOrgError(true);

      // Show detailed error message
      toast.error(i18n.errors.noOrganizationIdSupport);
    } else {
      setOrgError(false);
    }
  }, [orgID, navigate, user, authLoading, i18n.errors]);

  // Only enable queries if we have an organization ID
  const hasOrgID = Boolean(orgID) && !orgError;

  // Fetch available roles
  const {
    data: roles = [],
    isLoading: isRolesLoading,
    isError: isRolesError,
  } = useQuery({
    queryKey: ['roles', orgID],
    queryFn: () => {
      if (!orgID) {
        throw new Error(i18n.errors.noOrganizationIdRoles);
      }
      return fetchRoles(orgID);
    },
    staleTime: config.team.staleTimes.roles,
    enabled: hasOrgID,
    retry: 1,
  });

  // Fetch team members
  const {
    data: membersData = { results: [], count: 0 },
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['teamMembers', debouncedSearchText, roleFilter, statusFilter, orgID, page, pageSize],
    queryFn: async () => {
      if (!orgID) {
        throw new Error(i18n.errors.noOrganizationIdMemberships);
      }

      return fetchTeamMembers(orgID, {
        search: debouncedSearchText,
        role: roleFilter,
        status: statusFilter,
        page,
        page_size: pageSize,
      });
    },
    retry: 1,
    enabled: hasOrgID,
    placeholderData: prev => prev,
  });

  // Fetch last action for each user
  const userIds = useMemo(
    () =>
      membersData.results
        .map((member: Membership) => {
          // Extract user ID from different possible formats
          if (typeof member.user === 'string') return member.user;
          if (typeof member.user === 'number') return member.user.toString();
          if (member.user && typeof member.user === 'object' && 'id' in member.user)
            return member.user.id.toString();
          return '';
        })
        .filter(Boolean),
    [membersData.results]
  );

  // Fetch last product actions for all users
  const { data: userProductActions = {} } = useQuery({
    queryKey: ['userLastProductActions', userIds, orgID],
    queryFn: async () => {
      if (!orgID || userIds.length === 0) return {};
      return fetchUserProductActions(orgID, userIds);
    },
    enabled: userIds.length > 0 && !!orgID,
    staleTime: config.team.staleTimes.activity,
  });

  // Handle avatar update for a team member
  const handleAvatarUpdate = (_memberId: string, _newAvatarUrl: string) => {
    // Skip local state updates and just refetch data
    refetch();
    toast.success(i18n.success.avatarUpdated);
  };

  // Reset to page 1 when search text changes
  useEffect(() => {
    if (searchText !== debouncedSearchText) {
      setPage(1);
    }
  }, [searchText, debouncedSearchText]);

  // Handle page change for pagination
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Calculate total pages directly from the data
  const totalPages = Math.ceil(membersData.count / pageSize);

  const formatLastActivity = (member: Membership) => {
    try {
      // Log to help diagnose what data we're getting from API
      if (process.env.NODE_ENV === 'development') {
        logDebug('Member timestamps for activity formatting', {
          component: 'TeamPage',
          action: 'formatLastActivity',
          metadata: {
            memberId: member.id,
            last_login: member.last_login,
            updated_at: member.updated_at,
            created_at: member.created_at,
            status: member.status,
          },
        });
      }

      if (member.status === 'pending') {
        return member.invited_at
          ? i18n.activity.invitedAgo(formatDistanceToNow(new Date(member.invited_at)))
          : i18n.activity.recentlyInvited;
      } else {
        // Use the most recent timestamp from these fields
        const possibleDates: { field: string; date: Date }[] = [];

        // Add timestamps to array if they exist and are valid dates
        if (member.last_login) {
          try {
            const date = new Date(member.last_login);
            if (!isNaN(date.getTime())) possibleDates.push({ field: 'last_login', date });
          } catch {
            // Ignore invalid date format
          }
        }

        if (member.updated_at) {
          try {
            const date = new Date(member.updated_at);
            if (!isNaN(date.getTime())) possibleDates.push({ field: 'updated_at', date });
          } catch {
            // Ignore invalid date format
          }
        }

        if (member.created_at) {
          try {
            const date = new Date(member.created_at);
            if (!isNaN(date.getTime())) possibleDates.push({ field: 'created_at', date });
          } catch {
            // Ignore invalid date format
          }
        }

        if (possibleDates.length === 0) {
          return i18n.activity.activeRecently;
        }

        // Sort dates in descending order (newest first)
        possibleDates.sort((a, b) => b.date.getTime() - a.date.getTime());

        // Use the most recent date
        const mostRecent = possibleDates[0];
        return i18n.activity.lastActiveAgo(formatDistanceToNow(mostRecent.date));
      }
    } catch (e) {
      logError(
        'Failed to format member activity timestamp',
        {
          component: 'TeamPage',
          action: 'formatLastActivity',
          metadata: {
            memberId: member.id,
            memberStatus: member.status,
            error: e,
          },
        },
        {
          showToast: false,
          userFacing: false,
        }
      );
      return i18n.activity.activityStatusUnknown;
    }
  };

  const renderProductActionDescription = (activity: ProductActivity | null) => {
    if (!activity) return i18n.activity.noActionRecently;

    // Activity might come from different serializers with different field names
    const actionType = activity.type || activity.action;
    const message = activity.details || activity.message;
    const entityId = activity.entity_id;

    // Try to use localized product action descriptions
    if (actionType && entityId) {
      const productName = String(entityId);

      switch (actionType.toLowerCase()) {
        case 'created':
        case 'create':
          return i18n.activity.productCreated(productName);
        case 'updated':
        case 'update':
          return i18n.activity.productUpdated(productName);
        case 'deleted':
        case 'delete':
          return i18n.activity.productDeleted(productName);
        case 'archived':
        case 'archive':
          return i18n.activity.productArchived(productName);
        default:
          // Fall back to the message if available, or construct a basic description
          if (message) {
            return message;
          }
          return `${actionType} ${activity.entity || 'product'} ${productName}`;
      }
    }

    // Fall back to message or unknown action
    if (message) {
      return message;
    }

    return i18n.activity.unknownAction;
  };

  // Format the date of the last action in a user-friendly way
  const formatActionDate = (activity: ProductActivity | null) => {
    if (!activity) return '';

    // Handle both created_at and timestamp fields
    const dateString = activity.timestamp || activity.created_at;
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);
      const diffInWeeks = Math.floor(diffInDays / 7);
      const diffInMonths = Math.floor(diffInDays / 30);
      const diffInYears = Math.floor(diffInDays / 365);

      // Helper function to get language-specific plural suffix
      const getPluralSuffix = (count: number, lang: string) => {
        if (count === 1) return '';

        switch (lang) {
          case 'es': // Spanish
            return 'es'; // mes → meses, año → años
          case 'de': // German
            return 'e'; // Tag → Tage, Monat → Monate, Jahr → Jahre
          case 'pt': // Portuguese
            return 'es'; // mês → meses, ano → anos
          case 'en': // English
          default:
            return 's'; // day → days, month → months, year → years
        }
      };

      // Get current language from i18n
      const currentLang = i18n.i18n?.language || 'en'; // Fallback to 'en' if detection fails

      // Use proper localized relative time formatting
      if (diffInYears > 0) {
        return i18n.activity.yearsAgo({
          count: diffInYears,
          plural: getPluralSuffix(diffInYears, currentLang),
        });
      } else if (diffInMonths > 0) {
        return i18n.activity.monthsAgo({
          count: diffInMonths,
          plural: getPluralSuffix(diffInMonths, currentLang),
        });
      } else if (diffInWeeks > 0) {
        return i18n.activity.weeksAgo({
          count: diffInWeeks,
          plural: getPluralSuffix(diffInWeeks, currentLang),
        });
      } else if (diffInDays > 0) {
        return i18n.activity.daysAgo({
          count: diffInDays,
          plural: getPluralSuffix(diffInDays, currentLang),
        });
      } else if (diffInHours > 0) {
        return i18n.activity.hoursAgo(diffInHours);
      } else if (diffInMinutes > 0) {
        return i18n.activity.minutesAgo(diffInMinutes);
      } else {
        return i18n.activity.justNow;
      }
    } catch (e) {
      logError(
        'Failed to format activity date',
        {
          component: 'TeamPage',
          action: 'formatActionDate',
          metadata: {
            dateString: activity?.timestamp || activity?.created_at,
            error: e,
          },
        },
        {
          showToast: false,
          userFacing: false,
        }
      );
      return '';
    }
  };

  // Define the tabs array using translations
  const tabs = useMemo(
    () => [
      { value: 'all', label: i18n.tabs.allMembers },
      { value: 'active', label: i18n.tabs.active },
      { value: 'pending', label: i18n.tabs.pending },
    ],
    [i18n.tabs]
  );

  // Define the table columns using translations
  const tableColumns = useMemo(
    () => [
      { name: i18n.table.member, width: '35%' },
      { name: i18n.table.role, width: '10%' },
      { name: i18n.table.lastAction, width: '25%' },
      { name: i18n.table.lastActivity, width: '20%' },
      { name: i18n.table.actions, width: '10%', align: 'right' as const },
    ],
    [i18n.table]
  );

  // Loading state - show while auth is still loading or we're waiting for org ID
  if (authLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col space-y-2">
              <Skeleton className="h-8 w-1/3" />
              <Skeleton className="h-6 w-2/3" />
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state - when there's no organization ID available
  if (orgError) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">{i18n.errors.organizationError}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-6">
              <div className="mb-4 text-red-500">{i18n.errors.noOrganizationId}</div>
              <p className="mb-4 text-gray-600">{i18n.errors.noAssociatedOrganization}</p>
              {user?.is_staff && (
                <Button onClick={() => navigate('/app')}>{i18n.buttons.returnToDashboard}</Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Original loading state for team data
  if (isLoading || isRolesLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col space-y-2">
              <Skeleton className="h-8 w-1/3" />
              <Skeleton className="h-6 w-2/3" />
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Original error state for team data
  if (isError || isRolesError) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">{i18n.errors.dataError}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-6">
              <p className="text-red-500">{i18n.errors.failedToLoadTeamData}</p>
              <Button onClick={() => refetch()} variant="outline" className="mt-4">
                {i18n.buttons.retry}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="container mx-auto w-full max-w-full grow space-y-6 p-6"
      style={{ minWidth: '100%' }}
    >
      <div className="flex w-full flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="text-3xl font-bold text-enterprise-900 dark:text-white">
            {i18n.mainPage.pageTitle}
          </h1>
          <p className="mt-1 text-enterprise-600 dark:text-gray-300">
            {i18n.mainPage.pageDescription}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PermissionGuard permission="team.view">
            <Link to="/app/team/history">
              <Button variant="outline" className="flex items-center" size="sm">
                <History className="mr-2 size-4" />
                {i18n.buttons.history}
              </Button>
            </Link>
          </PermissionGuard>

          <PermissionGuard permission="team.invite">
            <Button
              variant="outline"
              onClick={() => setShowBulkInviteModal(true)}
              className="flex items-center"
              size="sm"
            >
              <Users className="mr-2 size-4" />
              {i18n.buttons.bulkInvite}
            </Button>

            <Button
              onClick={() => setShowInviteModal(true)}
              data-component="invite-button"
              className="flex items-center"
              size="sm"
            >
              <UserPlus className="mr-2 size-4" />
              {i18n.buttons.inviteMember}
            </Button>
          </PermissionGuard>
        </div>
      </div>

      <Card className="w-full">
        <CardContent className="p-0">
          <Tabs
            value={statusFilter}
            onValueChange={value => {
              setStatusFilter(value);
              setPage(1);
            }}
            className="w-full"
          >
            <div className="flex items-center justify-between border-b px-4 py-2">
              <TabsList>
                {tabs.map((tab: TabItem) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="data-[state=active]:bg-primary/10"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value={statusFilter} className="mt-0 p-0">
              <div className="flex flex-wrap items-center gap-4 border-b bg-muted/30 p-4">
                <div className="relative min-w-[240px] flex-1">
                  <Search className="absolute left-2.5 top-2.5 size-4 text-gray-500" />
                  <Input
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    placeholder={i18n.mainPage.searchPlaceholder}
                    className="w-full pl-9"
                    aria-label={i18n.mainPage.searchAriaLabel}
                  />
                </div>
                <div className="w-full sm:w-auto">
                  <Select
                    value={roleFilter}
                    onValueChange={value => {
                      setRoleFilter(value);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger
                      className="w-full sm:w-[180px]"
                      aria-label={i18n.filters.filterByRoleAriaLabel}
                    >
                      <SelectValue placeholder={i18n.filters.filterByRole} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{i18n.filters.allRoles}</SelectItem>
                      {roles.map(role => (
                        <SelectItem key={role.id} value={role.name}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Empty State */}
              {membersData.results.length === 0 && (
                <div className="p-8">
                  <EmptyState
                    title={i18n.emptyState.title}
                    description={i18n.emptyState.description}
                    buttonText={canInviteUsers ? i18n.buttons.inviteMember : ''}
                    buttonIcon={canInviteUsers ? <UserPlus className="mr-2 size-4" /> : undefined}
                    onButtonClick={() => canInviteUsers && setShowInviteModal(true)}
                  />
                </div>
              )}

              {/* Member List */}
              {Array.isArray(membersData.results) && membersData.results.length > 0 && (
                <div className="w-full overflow-x-auto">
                  <Table className="w-full table-fixed">
                    <TableHeader>
                      <TableRow>
                        {tableColumns.map((column, index) => (
                          <TableHead
                            key={index}
                            className={`w-[${column.width}]${column.align ? ` text-${column.align}` : ''}`}
                          >
                            {column.name}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {membersData.results.map(member => {
                        // Handle both formats - nested user object or flattened properties
                        const email =
                          member.user_email ||
                          (member.user && typeof member.user === 'object' ? member.user.email : '');
                        const name =
                          member.user_name ||
                          (member.user && typeof member.user === 'object'
                            ? member.user.name
                            : i18n.activity.user);
                        const avatar =
                          member.avatar_url ||
                          (member.user && typeof member.user === 'object'
                            ? member.user.avatar_url
                            : undefined);
                        const status = member.status || 'active';
                        const roleInfo = member.role || {
                          name: i18n.status.unknown,
                          description: '',
                        };

                        // Safely extract userId from different possible formats
                        let userId = '';
                        if (member.user) {
                          if (typeof member.user === 'string') {
                            userId = member.user;
                          } else if (typeof member.user === 'number') {
                            userId = member.user.toString();
                          } else if (typeof member.user === 'object' && 'id' in member.user) {
                            userId = String(member.user.id);
                          }
                        }

                        // Safely get roleId
                        const roleId = roleInfo.id !== undefined ? String(roleInfo.id) : '';

                        // Get the last action for this user
                        const lastProductAction =
                          userId && userProductActions ? userProductActions[userId] : null;

                        return (
                          <TableRow key={member.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center space-x-3">
                                <div className="relative">
                                  <Avatar
                                    name={name}
                                    avatarUrl={avatar}
                                    userId={userId}
                                    membershipId={member.id}
                                    showUploadButton={String(user?.id) === String(userId)}
                                    onAvatarUpdated={newUrl =>
                                      handleAvatarUpdate(member.id, newUrl)
                                    }
                                  />
                                  {status === 'active' ? (
                                    <span className="absolute bottom-0 right-0 size-2 rounded-full border-2 border-white bg-green-500" />
                                  ) : (
                                    <span className="absolute bottom-0 right-0 size-2 rounded-full border-2 border-white bg-gray-400" />
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium">{name}</div>
                                  <div className="text-sm text-gray-500">{email}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {status === 'active' ? (
                                <RoleDescriptionTooltip
                                  roleName={roleInfo.name}
                                  description={roleInfo.description || ''}
                                />
                              ) : (
                                <span className="rounded-full bg-amber-50 px-2 py-1 text-xs text-amber-600">
                                  {i18n.status.pending}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {lastProductAction ? (
                                <div>
                                  <div className="font-medium">
                                    {renderProductActionDescription(lastProductAction)}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {formatActionDate(lastProductAction)}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-gray-400">
                                  {i18n.activity.noActionRecently}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {formatLastActivity(member)}
                            </TableCell>
                            <TableCell className="text-right">
                              <PermissionGuard permission="team.change_role">
                                <ManageControls
                                  membershipId={member.id}
                                  status={status}
                                  currentRoleId={roleId}
                                  roleName={roleInfo.name}
                                  isCurrentUser={String(user?.id) === String(userId)}
                                />
                              </PermissionGuard>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t p-4">
                  <div className="text-sm text-gray-500">
                    {i18n.pagination.showing(
                      (page - 1) * pageSize + 1,
                      Math.min(page * pageSize, membersData.count),
                      membersData.count
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(1)}
                      disabled={page === 1}
                    >
                      {i18n.buttons.first}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                    >
                      {i18n.buttons.previous}
                    </Button>
                    <span className="mx-2">{i18n.pagination.page(page, totalPages)}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                    >
                      {i18n.buttons.next}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={page === totalPages}
                    >
                      {i18n.buttons.last}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Invite User Modal - only shown for users with invite permission */}
      {showInviteModal && canInviteUsers && roles.length > 0 && (
        <InviteUserModal
          show={showInviteModal}
          onHide={() => setShowInviteModal(false)}
          roles={roles}
          onSuccess={() => refetch()}
        />
      )}

      {/* Bulk Invite Modal - only shown for users with invite permission */}
      {showBulkInviteModal && canInviteUsers && roles.length > 0 && (
        <BulkInviteModal
          show={showBulkInviteModal}
          onHide={() => setShowBulkInviteModal(false)}
          roles={roles}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
};

export default TeamPage;
